import { useState, useEffect, useRef } from 'react'
import { NavLink, Link }               from 'react-router-dom'
import logoPrimary                      from '@assets/images/Logo/logo_Primary.png'
import { services }                     from '@data/services'
import { companyInfo }                  from '@data/companyInfo'

const SOCIAL_ICONS = {
  facebook:  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>,
  instagram: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/></svg>,
  linkedin:  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>,
  whatsapp:  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.859L.057 23.478a.75.75 0 00.916.919l5.701-1.464A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.695 9.695 0 01-4.953-1.355l-.355-.211-3.683.945.973-3.596-.231-.37A9.718 9.718 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>,
}

const SOCIALS = companyInfo.socials.map(s => ({ ...s, icon: SOCIAL_ICONS[s.platform] }))

// NAV_LINKS — Services has children for dropdown
const NAV_LINKS = [
  { label: 'Home',        to: '/' },
  { label: 'About Us',    to: '/about' },
  { label: 'Products',    to: '/products' },
  {
    label:    'Services',
    to:       '/services',
    children: services.map(s => ({ label: s.shortName, to: `/services/${s.slug}` })),
  },
  { label: 'News & Blog', to: '/blog' },
  { label: 'Contact',     to: '/contact' },
]

const SearchIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="M21 21l-4.35-4.35"/>
  </svg>
)

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6"  y2="18"/>
    <line x1="6"  y1="6" x2="18" y2="18"/>
  </svg>
)

// ── Desktop dropdown link ─────────────────────────────────────────────────────
function DropdownLink({ label, to, children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Close on outside click
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  return (
    <div
      ref={ref}
      style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'stretch' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Main link */}
      <NavLink
        to={to}
        className={({ isActive }) => `s-nav-link${isActive ? ' active' : ''}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        {label}
        {/* Chevron */}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </NavLink>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position:   'absolute',
          top:        '100%',
          left:       '50%',
          transform:  'translateX(-50%)',
          minWidth:   '220px',
          background: '#ffffff',
          border:     '1px solid #e8e8e8',
          borderTop:  '2px solid #007935',
          boxShadow:  '0 8px 24px rgba(0,0,0,0.10)',
          zIndex:     200,
        }}>
          {children.map(({ label: cLabel, to: cTo }, i) => (
            <Link
              key={cTo}
              to={cTo}
              onClick={() => setOpen(false)}
              style={{
                display:        'flex', alignItems: 'center', justifyContent: 'space-between',
                padding:        '12px 16px',
                fontFamily:     "'DM Sans', sans-serif",
                fontSize:       '13.5px', fontWeight: 400,
                color:          '#1a1a18', textDecoration: 'none',
                borderBottom:   i < children.length - 1 ? '1px solid #f0f0ee' : 'none',
                transition:     'background 0.15s ease, color 0.15s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#f5f5f3'
                e.currentTarget.style.color      = '#007935'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#ffffff'
                e.currentTarget.style.color      = '#1a1a18'
              }}
            >
              {cLabel}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Mobile drawer expandable services ─────────────────────────────────────────
function DrawerDropdown({ label, to, children, onClose }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div>
      {/* Parent row */}
      <div style={{
        display:        'flex', alignItems: 'center', justifyContent: 'space-between',
        padding:        '15px 20px',
        fontFamily:     "'DM Sans', sans-serif",
        fontSize:       '15px', fontWeight: 400,
        color:          '#1a1a18',
        borderBottom:   '1px solid #f0f0f0',
        cursor:         'pointer',
      }}
        onClick={() => setExpanded(p => !p)}
      >
        <Link to={to} onClick={onClose}
          style={{ color: '#1a1a18', textDecoration: 'none', flex: 1 }}>
          {label}
        </Link>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none"
          style={{ transition: 'transform 0.2s ease', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0, opacity: 0.4 }}>
          <path d="M5 2.5l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Children — expand below */}
      {expanded && (
        <div style={{ background: '#f9f9f7', borderBottom: '1px solid #f0f0f0' }}>
          {children.map(({ label: cLabel, to: cTo }) => (
            <Link
              key={cTo}
              to={cTo}
              onClick={onClose}
              style={{
                display:        'flex', alignItems: 'center', gap: '8px',
                padding:        '11px 20px 11px 32px',
                fontFamily:     "'DM Sans', sans-serif",
                fontSize:       '13.5px', fontWeight: 400,
                color:          '#007935', textDecoration: 'none',
                borderBottom:   '1px solid #f0f0f0',
                transition:     'background 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f0ee'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {cLabel}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main Navbar ───────────────────────────────────────────────────────────────
export default function Navbar() {
  const [scrolled,   setScrolled]   = useState(false)
  const [menuOpen,   setMenuOpen]   = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQ,    setSearchQ]    = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  useEffect(() => {
    const fn = () => { if (window.innerWidth >= 1024) setMenuOpen(false) }
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  useEffect(() => {
    if (searchOpen) setTimeout(() => inputRef.current?.focus(), 60)
    else setSearchQ('')
  }, [searchOpen])

  return (
    <>
      <style>{`
        .s-topbar { position:fixed; top:0; left:0; right:0; height:36px; background:#007935; z-index:101; transition:transform 0.3s ease; }
        .s-topbar.hide { transform:translateY(-100%); }
        .s-topbar-inner { max-width:var(--container-max); margin:0 auto; padding:0 var(--container-padding); height:100%; display:flex; align-items:center; justify-content:space-between; }

        .s-header { position:fixed; left:0; right:0; width:100%; height:80px; background:#ffffff; z-index:100; border-bottom:1px solid #e8e8e8; transition:top 0.3s ease, box-shadow 0.3s ease; }
        .s-header.at-top   { top:36px; }
        .s-header.scrolled { top:0; box-shadow:0 2px 16px rgba(0,0,0,0.09); border-bottom:none; }
        .s-header-inner { max-width:var(--container-max); margin:0 auto; padding:0 var(--container-padding); height:100%; display:flex; align-items:center; gap:0; }

        .s-desktop-nav { display:none; align-items:stretch; height:100%; flex:1; margin-left:24px; }

        .s-nav-link { display:inline-flex; align-items:center; padding:0 16px; font-family:'DM Sans',sans-serif; font-size:15px; font-weight:400; letter-spacing:0.01em; white-space:nowrap; text-decoration:none; color:#1a1a18; border-bottom:3px solid transparent; border-top:3px solid transparent; transition:color 0.2s ease, border-color 0.2s ease; }
        .s-nav-link:hover  { color:#007935; border-bottom-color:#007935; }
        .s-nav-link.active { color:#007935; font-weight:500; border-bottom-color:#007935; }

        .s-search-wrap { display:flex; align-items:center; flex-shrink:0; height:100%; gap:0; }
        .s-search-input-wrap { overflow:hidden; width:0; opacity:0; transition:width 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease; height:42px; display:flex; align-items:center; background:#f5f5f3; border:1px solid #e0e0da; border-right:none; }
        .s-search-input-wrap.open { width:clamp(180px, 22vw, 300px); opacity:1; }
        .s-search-input-wrap input { width:100%; border:none; background:transparent; padding:0 14px; height:100%; font-family:'DM Sans',sans-serif; font-size:14px; color:#1a1a18; outline:none; }
        .s-search-input-wrap input::placeholder { color:#b8b8b0; }
        .s-search-btn { width:42px; height:42px; flex-shrink:0; display:flex; align-items:center; justify-content:center; background:#007935; border:none; cursor:pointer; color:#ffffff; align-self:center; transition:background 0.2s ease; }
        .s-search-btn:hover { background:#065e32; }

        .s-hamburger { display:none; align-items:center; justify-content:center; width:40px; height:40px; background:transparent; border:none; cursor:pointer; color:#1a1a18; flex-shrink:0; transition:color 0.2s ease; }
        .s-hamburger:hover { color:#007935; }

        @media (min-width:1024px) { .s-desktop-nav { display:flex !important; } .s-hamburger { display:none !important; } .s-search-wrap { display:flex !important; } .s-tb-right { display:flex !important; } }
        @media (max-width:1023px) { .s-desktop-nav { display:none !important; } .s-hamburger { display:flex !important; } .s-search-wrap { display:none !important; } .s-tb-right { display:none !important; } .s-header-inner { padding:0 16px; } }
        @media (max-width:1023px) { .s-logo-link { flex-direction:column !important; align-items:center !important; gap:2px !important; } .s-logo-link img { max-width:48px !important; width:48px !important; } .s-logo-text { font-size:10px !important; text-align:center; } }

        .s-overlay { position:fixed; inset:0; z-index:98; background:rgba(0,0,0,0.45); transition:opacity 0.3s ease; }
        .s-overlay.closed { opacity:0; pointer-events:none; }
        .s-overlay.open   { opacity:1; pointer-events:auto; }

        .s-drawer { position:fixed; top:0; left:0; bottom:0; z-index:99; width:min(280px, 85vw); background:#ffffff; display:flex; flex-direction:column; overflow-y:auto; box-shadow:4px 0 24px rgba(0,0,0,0.12); transition:transform 0.35s cubic-bezier(0.4,0,0.2,1); }
        .s-drawer.closed { transform:translateX(-100%); }
        .s-drawer.open   { transform:translateX(0); }
        .s-drawer-top { display:flex; align-items:center; justify-content:space-between; padding:10px 16px; border-bottom:1px solid #eeeeee; flex-shrink:0; min-height:52px; }
        .s-drawer-link { display:flex; align-items:center; justify-content:space-between; padding:13px 20px; font-family:'DM Sans',sans-serif; font-size:15px; font-weight:400; color:#1a1a18; text-decoration:none; border-bottom:1px solid #f0f0f0; transition:color 0.15s ease, background 0.15s ease; }
        .s-drawer-link:hover  { color:#007935; background:#f9f9f7; }
        .s-drawer-link.active { color:#007935; font-weight:600; border-left:3px solid #007935; padding-left:17px; }

        .s-drawer-search { display:flex; align-items:center; border:1px solid #dddddd; margin:16px 20px; flex-shrink:0; }
        .s-drawer-search input { flex:1; border:none; background:transparent; padding:11px 12px; font-family:'DM Sans',sans-serif; font-size:14px; color:#333; outline:none; }
        .s-drawer-search input::placeholder { color:#bbbbbb; }
        .s-drawer-search button { width:44px; height:44px; flex-shrink:0; background:#007935; border:none; cursor:pointer; color:#ffffff; display:flex; align-items:center; justify-content:center; }
        .s-drawer-search button:hover { background:#065e32; }

        .s-social-btn { width:34px; height:34px; display:flex; align-items:center; justify-content:center; background:#f0f0f0; color:#666; text-decoration:none; transition:background 0.2s ease, color 0.2s ease; }
        .s-social-btn:hover { background:#007935; color:#ffffff; }
      `}</style>

      {/* TOP BAR */}
      <div className={`s-topbar${scrolled ? ' hide' : ''}`}>
        <div className="s-topbar-inner">
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'11px', fontWeight:600, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(255,255,255,0.92)' }}>
            Global quality,Local Care
          </span>
          <div className="s-tb-right" style={{ alignItems:'center', gap:'14px' }}>
            <a href="tel:+94718919034" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'11px', fontWeight:500, color:'rgba(255,255,255,0.85)', textDecoration:'none' }}>
              +94 718 919 034
            </a>
            <div style={{ width:'1px', height:'10px', background:'rgba(255,255,255,0.3)' }} />
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              {SOCIALS.map(({ label, href, icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  style={{ color:'rgba(255,255,255,0.75)', display:'flex', transition:'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.75)'}>
                  {icon}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header className={`s-header${scrolled ? ' scrolled' : ' at-top'}`}>
        <div className="s-header-inner">

          {/* Logo LEFT — wrapper takes flex:1 to push nav/hamburger right */}
          <div style={{ flex:'1', flexShrink:0, display:'flex', alignItems:'center' }}>

            {/* ── CURRENT: text left, logo right (row) on desktop; column on mobile ── */}
            <Link to="/" onClick={() => setMenuOpen(false)}
              className="s-logo-link"
              style={{ display:'inline-flex', flexDirection:'row', alignItems:'center', gap:'10px', textDecoration:'none' }}>
              <img src={logoPrimary} alt="Salient Agrinutrition"
                style={{ height:'auto', width:'auto', objectFit:'contain', maxWidth:'90px' }} />
              <span className="s-logo-text" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'14px', fontWeight:600, color:'#007935', letterSpacing:'0.08em', textTransform:'uppercase', lineHeight:1.2 }}>Salient<br/>Agrinutrition</span>
            </Link>

            {/* ── PREVIOUS: logo on top, text below (column) both desktop+mobile — uncomment to revert ──
            <Link to="/" onClick={() => setMenuOpen(false)}
              className="s-logo-link"
              style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:'2px', textDecoration:'none' }}>
              <img src={logoPrimary} alt="Salient Agrinutrition"
                style={{ height:'auto', width:'auto', objectFit:'contain', maxWidth:'64px' }} />
              <span className="s-logo-text" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'11px', fontWeight:600, color:'#007935', letterSpacing:'0.08em', textTransform:'uppercase', lineHeight:1.2, textAlign:'center' }}>Salient<br/>Agrinutrition</span>
            </Link>
            ── */}

          </div>

          {/* Desktop nav — Services has dropdown */}
          <nav className="s-desktop-nav">
            {NAV_LINKS.map(({ label, to, children }) => (
              children
                ? <DropdownLink key={to} label={label} to={to} children={children} />
                : (
                  <NavLink key={to} to={to} end={to === '/'}
                    className={({ isActive }) => `s-nav-link${isActive ? ' active' : ''}`}>
                    {label}
                  </NavLink>
                )
            ))}
          </nav>

          {/* Search expand + green button */}
          <div className="s-search-wrap">
            <div className={`s-search-input-wrap${searchOpen ? ' open' : ''}`}>
              <input
                ref={inputRef} type="text" placeholder="Search ..."
                value={searchQ} onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setSearchOpen(false) }}
              />
            </div>
            <button className="s-search-btn" onClick={() => setSearchOpen(p => !p)}
              aria-label={searchOpen ? 'Close search' : 'Search'}>
              {searchOpen ? <CloseIcon /> : <SearchIcon />}
            </button>
          </div>

          {/* Hamburger FAR RIGHT — mobile */}
          <button className="s-hamburger" onClick={() => setMenuOpen(p => !p)} aria-label="Open menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6"  x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>

        </div>
      </header>

      {/* Spacer */}
      <div style={{ height: scrolled ? '80px' : '116px', transition:'height 0.3s ease' }} />

      {/* Overlay */}
      <div className={`s-overlay${menuOpen ? ' open' : ' closed'}`} onClick={() => setMenuOpen(false)} />

      {/* LEFT DRAWER */}
      <div className={`s-drawer${menuOpen ? ' open' : ' closed'}`}>

        {/* Slim close bar — no logo (topbar + main header already show it) */}
        <div className="s-drawer-top">
          <span style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: '10px', fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#88877f',
          }}>
            Menu
          </span>
          <button onClick={() => setMenuOpen(false)}
            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#555', display:'flex', alignItems:'center', padding:'4px' }}>
            <CloseIcon />
          </button>
        </div>

        {/* Nav — Services is expandable dropdown */}
        <nav style={{ flex:1 }}>
          {NAV_LINKS.map(({ to, label, children }) => (
            children
              ? <DrawerDropdown
                  key={to} label={label} to={to} children={children}
                  onClose={() => setMenuOpen(false)}
                />
              : (
                <NavLink key={to} to={to} end={to==='/'} onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `s-drawer-link${isActive ? ' active' : ''}`}>
                  <span>{label}</span>
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ opacity:0.3, flexShrink:0 }}>
                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </NavLink>
              )
          ))}
        </nav>

        {/* Contact info + socials */}
        <div style={{ padding:'16px 20px', borderTop:'1px solid #f0f0f0' }}>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'10px', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#999', margin:'0 0 8px' }}>Call Us</p>
          <a href="tel:+94718919034" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'13px', fontWeight:600, color:'#222', textDecoration:'none', display:'block', marginBottom:'2px' }}>+94 718 919 034</a>
          <a href="tel:+94703813000" style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'13px', fontWeight:600, color:'#222', textDecoration:'none', display:'block', marginBottom:'16px' }}>+94 703 813 000</a>
          <div style={{ display:'flex', gap:'6px' }}>
            {SOCIALS.map(({ label, href, icon }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="s-social-btn">{icon}</a>
            ))}
          </div>
        </div>

        {/* Search in drawer */}
        <div className="s-drawer-search">
          <input type="text" placeholder="Search ..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
          <button aria-label="Search"><SearchIcon /></button>
        </div>

      </div>
    </>
  )
}